import React, { useState, useEffect } from "react";
import CloseIcon from '@mui/icons-material/Close';
import { Checkbox, Stack, TableCell, TextField, Typography } from '@mui/material';
import { PausedIndicator } from '../../../Components/PausedIndicator';
import { SkuFulfillmentTypeSelect } from "../components/SkuFulfillmentTypeSelect";
import { SkuOfferingStatusSelect } from '../components/SkuOfferingStatusSelect';
import { DistributorInMarketDatePicker } from "../components/DistributorInMarketDatePicker";
import { useProductSettingsContext } from "../context/ProductSettingsContext";
import PropTypes from 'prop-types';

const enableInMarketDate = process.env.REACT_APP_ENABLE_IN_MARKET_DATE === 'true';
const enablePauseIndicator = process.env.REACT_APP_ENABLE_PAUSE_INDICATOR === 'true';

export const DistributorTableRow = ({
    row,
    handleUpdateRow,
    onSaveProductSettingsDistributorOverride,
    isChanged,
}) => {
    const { onRowSelectClick, isRowSelected } = useProductSettingsContext();
    const [originalRow, setOriginalRow] = useState(row);

    // Update originalRow when row changes and contains complete data
    useEffect(() => {
        if (row && (
            !originalRow?.skuOfferingStatus && row.skuOfferingStatus ||
            !originalRow?.skuFulfillmentType && row.skuFulfillmentType ||
            JSON.stringify(row) !== JSON.stringify(originalRow)
        )) {
            setOriginalRow({ ...row });
        }
    }, [row, originalRow]);

    const onBlurHandler = async () => {
        await onSaveProductSettingsDistributorOverride(row, originalRow);
        await handleUpdateRow(row);
        setOriginalRow(row);
    };

    const onKeyPressHandler = async (event) => {
        if (event.key === "Enter") {
            await onSaveProductSettingsDistributorOverride(row, originalRow);
            setOriginalRow(row);
        }
    };

    return (<React.Fragment>
        <TableCell padding="checkbox">
            <Checkbox
                id={row?.id}
                color="primary"
                disabled={row?.legal === false}
                checked={isRowSelected(row)}
                onChange={event => onRowSelectClick(row, event.target.checked)} />
        </TableCell>
        <TableCell>
            <Stack direction='row' alignItems='center' spacing={1}>
                {enablePauseIndicator ? (row?.paused ? <PausedIndicator size={20} /> : <span style={{ width: 20, display: 'inline-block' }} />) : null}
                <Typography>{`${row?.sapId} - ${row?.name}`}</Typography>
            </Stack>
        </TableCell>
        <TableCell align="center" sx={{ padding: '0px' }}>
            {row?.legal === false ? (<CloseIcon />) : <></>}
        </TableCell>
        <TableCell align="center" sx={{ padding: '0px' }}>
            <TextField
                placeholder="Default"
                value={row.palletQuantity}
                size="small"
                type="number"
                disabled={row?.legal === false}
                onKeyPress={onKeyPressHandler}
                onChange={async (event) => {
                    if (!Number.isInteger(Number.parseFloat(event.target.value)) && event.target.value !== "") return;
                    await handleUpdateRow({ ...row, palletQuantity: event.target.value })
                }}
                onBlur={onBlurHandler}
                sx={{ width: 110 }}
            />
        </TableCell>
        <TableCell align="center" sx={{ padding: '0px' }}>
            <TextField
                placeholder="Default"
                value={row.palletDivisor}
                size="small"
                type="number"
                disabled={row?.legal === false}
                onKeyPress={onKeyPressHandler}
                onChange={async (event) => {
                    if (!Number.isInteger(Number.parseFloat(event.target.value)) && event.target.value !== "") return;
                    await handleUpdateRow({ ...row, palletDivisor: event.target.value })
                }}
                onBlur={onBlurHandler}
                sx={{ width: 110 }}
            />
        </TableCell>
        <TableCell align="center" sx={{ padding: '0px' }}>
            <TextField
                placeholder="Default"
                value={row.unitWeight}
                size="small"
                type="number"
                disabled={row?.legal === false}
                onKeyPress={onKeyPressHandler}
                onChange={async (event) => {
                    if (Number.isNaN(event.target.value) && event.target.value !== "") return;
                    await handleUpdateRow({ ...row, unitWeight: event.target.value })
                }}
                onBlur={onBlurHandler}
                sx={{ width: 110 }}
            />
        </TableCell>
        <TableCell align="center">
            <SkuOfferingStatusSelect
                row={row}
                originalRow={originalRow}
                handleUpdateRow={handleUpdateRow}
            />
        </TableCell>
        <TableCell align="center">
            <SkuFulfillmentTypeSelect
                handleUpdateRow={handleUpdateRow}
                isChanged={isChanged}
                row={row}
                originalRow={originalRow} />
        </TableCell>
        {enableInMarketDate && (
            <TableCell align="center" sx={{ padding: '0 6px' }}>
                <DistributorInMarketDatePicker row={row} handleUpdateRow={handleUpdateRow} />
            </TableCell>
        )}
    </React.Fragment>)
}

DistributorTableRow.propTypes = {
    row: PropTypes.object,
    handleUpdateRow: PropTypes.func,
    onSaveProductSettingsDistributorOverride: PropTypes.func,
    isChanged: PropTypes.func,
}